/**
 * Happy-flow "Mark as Completed" confirmation with linked sibling orders table.
 * @module happyFlowModal
 */
import { Modal } from "@risalabs_frontend_org/oasis-ui-kit";
import { useSelector } from "react-redux";
import { getBadgePropsForAuthStatus } from "../../../enums/medicalPaOrdersAuthStatus";
import { ModalId } from "../../../enums/modalId";
import { RootState } from "../../../redux/store/store";
import OutlineWarningIcon from "../../../svg/outline-warning-icon";
import { useLinkedOrdersModalView } from "../../expandableTableForOrderAuth/hooks/useLinkedOrdersModalView";
import OrderReassignmentWithCheckbox from "../../orderAssignmentWithCheckbox/orderReassignmentWithCheckbox";
import TextWithAuthStatus from "../../textWithAuthStatus/TextWithAuthStatus";
import RenderSiblingOrderContentBody from "../updateCigNoteConfirmationDialog/renderSiblingOrderContentBody";

const DEFAULT_TITLE = "Mark as Completed";

/** Props for {@link HappyFlowModal}. */
interface HappyFlowModalProps {
  onClose: () => void;
  /** Confirms save; receives selected linked order ids when rows are checked. */
  onSave: (selectedOrderIds?: string[]) => void;
  isLoading: boolean;
  showReassignmentCheckbox: boolean;
  onSelectedProviderChange: (provider: { id: string; name: string }) => void;
  authStatus: string;
}

/**
 * Confirms mark-as-complete with legacy copy and linked sibling orders below.
 *
 * @param props - Save/close handlers, loading flag, and reassignment config
 * @returns Happy-flow confirmation modal
 */
const HappyFlowModal = (props: HappyFlowModalProps) => {
  const authStatusOptions = useSelector(
    (state: RootState) => state.authStatusOptions,
  );
  const {
    expandableTableData,
    siblingOrdersError,
    hasLinkedOrders,
    selectedSiblingOrderIds,
  } = useLinkedOrdersModalView();

  const handleSave = () => {
    props.onSave(
      expandableTableData.length > 0 && selectedSiblingOrderIds.length > 0
        ? selectedSiblingOrderIds
        : undefined,
    );
  };

  const authStatus = getBadgePropsForAuthStatus(
    authStatusOptions.data ?? [],
    props.authStatus,
  )?.text;

  return (
    <Modal
      dialogId={ModalId.HAPPY_FLOW_MODAL}
      title={DEFAULT_TITLE}
      onClose={props.onClose}
      onSave={handleSave}
      cancelText="No, Cancel"
      disableSave={props.isLoading}
      saveButtonText={props.isLoading ? "Saving..." : "Yes, Confirm"}
      maxHeightPercentage={hasLinkedOrders ? 80 : undefined}
    >
      <div className="flex flex-col gap-4 py-2">
        <div className="flex flex-col items-center justify-center gap-4">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primaryGray-16">
            <OutlineWarningIcon />
          </div>
          <div className="w-4/5 text-wrap text-center font-normal text-primaryGray-1">
            Are you sure you want to mark the order as complete, this will
            update the status ?
          </div>
          <TextWithAuthStatus status={authStatus} />
          {props.showReassignmentCheckbox && (
            <OrderReassignmentWithCheckbox
              onSelectedProviderChange={props.onSelectedProviderChange}
            />
          )}
        </div>

        <RenderSiblingOrderContentBody
          expandedContentType="table"
          expandableTableData={expandableTableData}
          siblingOrdersLoading={false}
          siblingOrdersError={siblingOrdersError}
          descriptionText="Complete following linked orders as well?"
        />
      </div>
    </Modal>
  );
};

export default HappyFlowModal;
